import { useState } from 'react';
import { useOrderStore, useResourceStore } from '@/store';
import { RushOrder } from '@/types';

interface RushOrderFormProps {
  onClose: () => void;
}

export function RushOrderForm({ onClose }: RushOrderFormProps) {
  const mainWorkOrders = useOrderStore((state) => state.mainWorkOrders);
  const subWorkOrders = useOrderStore((state) => state.subWorkOrders);
  const addRushOrder = useOrderStore((state) => state.addRushOrder);
  const products = useResourceStore((state) => state.products);
  const persons = useResourceStore((state) => state.persons);
  const machines = useResourceStore((state) => state.machines);
  const materials = useResourceStore((state) => state.materials);

  const [formData, setFormData] = useState({
    productId: '',
    productName: '',
    totalQuantity: 100,
    customer: '',
    batchNo: '',
    orderDate: new Date().toISOString().slice(0, 10),
    originalDeliveryDate: '',
    requestedDeliveryDate: '',
    reason: '',
    priority: '重要' as RushOrder['priority'],
  });

  // 範例已載入提示狀態
  const [showExampleLoaded, setShowExampleLoaded] = useState(false);

  // 三種範例場景
  const exampleScenarios = [
    {
      name: '人員衝突範例',
      productId: 'P001',
      productName: '高精度壓力感測器',
      totalQuantity: 200,
      customer: '急單客戶-A科技有限公司',
      priority: '緊急' as RushOrder['priority'],
      originalDeliveryDate: '2026-04-15',
      requestedDeliveryDate: '2026-02-01',
      reason: '客戶生產線緊急需求，原有供應商無法按時交貨。此插單會與現有工單 MW-2026-001-A/MW-2026-001-B 產生人員衝突（E001, E003, E004, E010），需要安排加班或調整班次。',
    },
    {
      name: '機台衝突範例',
      productId: 'P002',
      productName: '鋁合金外殼',
      totalQuantity: 500,
      customer: '急單客戶-B電子公司',
      priority: '緊急' as RushOrder['priority'],
      originalDeliveryDate: '2026-04-01',
      requestedDeliveryDate: '2026-02-10',
      reason: '新產品上市需要，外殼供應鏈出問題。此插單會與現有工單 MW-2026-002 產生機台衝突（M001: 砂型鑄造機、M002: 精密加工機、M008: 切割機），需要調整生產排程。',
    },
    {
      name: '物料不足範例',
      productId: 'P003',
      productName: '智慧控制模組',
      totalQuantity: 150,
      customer: '急單客戶-C智慧家居',
      priority: '緊急' as RushOrder['priority'],
      originalDeliveryDate: '2026-04-20',
      requestedDeliveryDate: '2026-02-15',
      reason: '智能家居訂單激增，需要補貨。此插單會消耗 MAT008（陽極藥水），目前庫存僅 8 桶（安全庫存 3 桶），同時也會與工單 MW-2026-002 產生物料衝突，建議緊急採購。',
    },
  ];

  // 載入隨機範例
  const handleLoadExample = () => {
    const randomIndex = Math.floor(Math.random() * exampleScenarios.length);
    const scenario = exampleScenarios[randomIndex];

    setFormData({
      ...formData,
      productId: scenario.productId,
      productName: scenario.productName,
      totalQuantity: scenario.totalQuantity,
      customer: scenario.customer,
      batchNo: `EXAMPLE-${Date.now()}`,
      originalDeliveryDate: scenario.originalDeliveryDate,
      requestedDeliveryDate: scenario.requestedDeliveryDate,
      reason: scenario.reason,
      priority: scenario.priority,
    });

    // 顯示載入成功訊息
    setShowExampleLoaded(true);
    setTimeout(() => setShowExampleLoaded(false), 2000);
  };

  // 分析資源衝突
  const analyzeConflicts = () => {
    const conflicts = {
      persons: [] as string[],
      machines: [] as string[],
      materials: [] as string[],
    };

    // 找出時間範圍內衝突的資源
    const requestedStart = new Date(formData.requestedDeliveryDate);
    const requestedEnd = new Date(requestedStart);
    requestedEnd.setMonth(requestedEnd.getMonth() + 1);

    mainWorkOrders.forEach((mwo) => {
      const mwoStart = new Date(mwo.plannedStart);
      const mwoEnd = new Date(mwo.plannedEnd);

      // 檢查時間是否重疊
      if (requestedStart < mwoEnd && requestedEnd > mwoStart) {
        const relatedSubOrders = subWorkOrders.filter((swo) => swo.mainOrderId === mwo.id);
        relatedSubOrders.forEach((swo) => {
          // 收集衝突的人員
          swo.assignedPersons.forEach((personId) => {
            if (!conflicts.persons.includes(personId)) {
              conflicts.persons.push(personId);
            }
          });
          // 收集衝突的機台
          if (swo.machineId && !conflicts.machines.includes(swo.machineId)) {
            conflicts.machines.push(swo.machineId);
          }
          // 收集衝突的物料
          swo.requiredMaterials.forEach((materialId) => {
            const material = materials.find((m) => m.id === materialId);
            if (material && material.stockQuantity <= material.safetyStock && !conflicts.materials.includes(materialId)) {
              conflicts.materials.push(materialId);
            }
          });
        });
      }
    });

    return conflicts;
  };

  const conflicts = analyzeConflicts();

  const getConflictingOrders = () => {
    const conflictingOrders: string[] = [];
    const requestedStart = new Date(formData.requestedDeliveryDate);
    const requestedEnd = new Date(requestedStart);
    requestedEnd.setMonth(requestedEnd.getMonth() + 1);

    mainWorkOrders.forEach((mwo) => {
      const mwoStart = new Date(mwo.plannedStart);
      const mwoEnd = new Date(mwo.plannedEnd);
      if (requestedStart < mwoEnd && requestedEnd > mwoStart) {
        conflictingOrders.push(mwo.id);
      }
    });

    return conflictingOrders;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newRushOrder: RushOrder = {
      id: `RUSH-${Date.now()}`,
      ...formData,
      orderDate: new Date(formData.orderDate).toISOString(),
      originalDeliveryDate: new Date(formData.originalDeliveryDate).toISOString(),
      requestedDeliveryDate: new Date(formData.requestedDeliveryDate).toISOString(),
      status: '待確認',
      conflictingOrders: getConflictingOrders(),
      resourceConflicts: conflicts,
      recommendedActions: generateRecommendations(),
      impactAssessment: generateImpactAssessment(),
    };

    // 保存到 store
    addRushOrder(newRushOrder);
    alert('插單已建立！請查看衝突分析結果。');
    onClose();
  };

  const generateRecommendations = (): string[] => {
    const recommendations: string[] = [];

    if (conflicts.persons.length > 0) {
      recommendations.push('安排加班以增加人力產能');
    }
    if (conflicts.machines.length > 0) {
      recommendations.push('調整生產順序或增加機台產能');
    }
    if (conflicts.materials.length > 0) {
      recommendations.push('緊急採購不足的物料');
    }

    const conflictingOrders = getConflictingOrders();
    if (conflictingOrders.length > 0) {
      recommendations.push(`考慮延後以下訂單：${conflictingOrders.slice(0, 2).join(', ')}`);
    }

    return recommendations;
  };

  const generateImpactAssessment = () => {
    const conflictingOrders = getConflictingOrders();
    return conflictingOrders.map((orderId) => ({
      orderId,
      impactType: '延遲' as const,
      delayDays: 7,
      canPause: Math.random() > 0.5,
      notes: '需進行詳細評估',
    }));
  };

  const handleProductChange = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setFormData({
        ...formData,
        productId,
        productName: product.name,
      });
    }
  };

  const getPersonName = (id: string) => persons.find((p) => p.id === id)?.name || id;
  const getMachineName = (id: string) => machines.find((m) => m.id === id)?.name || id;
  const getMaterialName = (id: string) => materials.find((m) => m.id === id)?.name || id;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">🚨 新增插單</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* 產品選擇 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                產品 <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.productId}
                onChange={(e) => handleProductChange(e.target.value)}
                className="input"
              >
                <option value="">請選擇產品</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.id} - {product.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 客戶名稱 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                客戶名稱 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.customer}
                onChange={(e) => setFormData({ ...formData, customer: e.target.value })}
                className="input"
                placeholder="輸入客戶名稱"
              />
            </div>

            {/* 數量 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                數量 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                required
                min="1"
                value={formData.totalQuantity}
                onChange={(e) => setFormData({ ...formData, totalQuantity: Number(e.target.value) })}
                className="input"
              />
            </div>

            {/* 優先級 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                優先級 <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as RushOrder['priority'] })}
                className="input"
              >
                <option value="重要">重要</option>
                <option value="緊急">緊急</option>
              </select>
            </div>

            {/* 原交期 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                原定交期 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.originalDeliveryDate}
                onChange={(e) => setFormData({ ...formData, originalDeliveryDate: e.target.value })}
                className="input"
              />
            </div>

            {/* 要求交期 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                要求交期 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.requestedDeliveryDate}
                onChange={(e) => setFormData({ ...formData, requestedDeliveryDate: e.target.value })}
                className="input"
              />
            </div>
          </div>

          {/* 插單原因 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              插單原因 <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="input"
              rows={2}
              placeholder="請說明插單原因"
            />
          </div>

          {/* 衝突分析顯示 */}
          {formData.requestedDeliveryDate && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="text-lg font-semibold text-red-800 mb-3">⚠️ 資源衝突分析</h3>

              <div className="space-y-3">
                {/* 衝突人員 */}
                {conflicts.persons.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700">衝突人員：</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {conflicts.persons.map((personId) => (
                        <span
                          key={personId}
                          className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs"
                        >
                          {getPersonName(personId)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 衝突機台 */}
                {conflicts.machines.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700">衝突機台：</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {conflicts.machines.map((machineId) => (
                        <span
                          key={machineId}
                          className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs"
                        >
                          {getMachineName(machineId)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 衝突物料 */}
                {conflicts.materials.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700">庫存不足物料：</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {conflicts.materials.map((materialId) => (
                        <span
                          key={materialId}
                          className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs"
                        >
                          {getMaterialName(materialId)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 衝突訂單 */}
                {getConflictingOrders().length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-red-700">衝突工單：</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {getConflictingOrders().map((orderId) => (
                        <span
                          key={orderId}
                          className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs"
                        >
                          {orderId}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 建議方案 */}
                {generateRecommendations().length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-blue-700">建議應對方案：</p>
                    <ul className="mt-1 space-y-1">
                      {generateRecommendations().map((rec, index) => (
                        <li key={index} className="text-sm text-blue-600">
                          • {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 按鈕 */}
          <div className="flex justify-between items-center pt-4">
            {/* 左側：載入範例按鈕 */}
            <button
              type="button"
              onClick={handleLoadExample}
              className="px-4 py-2 bg-sky-200 text-sky-800 rounded-lg hover:bg-sky-300 transition-colors font-medium"
            >
              📋 載入範例
            </button>

            {/* 右側：取消與確認按鈕 */}
            <div className="flex space-x-3">
              {showExampleLoaded && (
                <span className="px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm flex items-center">
                  ✓ 範例已載入
                </span>
              )}
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
              >
                取消
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                確認插單
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
